export const COLUMN_DATA_TYPES = ["Text", "Number", "Date", "Boolean"] as const;
export type ColumnDataType = (typeof COLUMN_DATA_TYPES)[number];

export interface DerivedTableColumn {
  id: string;
  /** Unset (null) until the author picks one — a new column has no default type. */
  dataType: ColumnDataType | null;
  /** the name of the column **/
  name: string;
  /** Only meaningful (and only settable) when dataType is "Number". */
  aggregate: boolean;
}

/**
 * Defines a row in the derived table
 */
export interface DerivedTableTabRow {
  id: string;
  /** Cell values keyed by DerivedTableColumn.id, so reordering/renaming columns doesn't lose data.*/
  [columnId: string]: string;
}

/**
 * A row definition - defines the set of rows that should be generated for each instance of the specified entity
 */
export interface DerivedTableTab {
  id: string;
  /** The text of the row definition's entity. */
  entity: string;
  name: string;
  rows: DerivedTableTabRow[];
}

export const COMPARATORS = ["=", ">", "<", "<=", ">="] as const;
export type Comparator = (typeof COMPARATORS)[number];

/**
 * One row-exclusion condition. Rows are DROPPED (not kept) when `column <operator> value` holds.
 * When a table has more than one filter, they combine with OR: a row is excluded if any single
 * filter's condition is true.
 */
export interface DerivedTableFilter {
  id: string;
  /** DerivedTableColumn.id this filter targets - a reference by id, like DerivedTableTabRow cells, so renaming/reordering columns doesn't lose it. */
  columnId: string;
  operator: Comparator;
  /** Raw authored text, parsed the same way a table cell is: a literal value, or an attribute reference. */
  value: string;
}

/**
 * One ordering rule. Rows are sorted by the first rule; each subsequent rule only breaks ties
 * left by the rules before it.
 */
export interface DerivedTableOrdering {
  id: string;
  /** DerivedTableColumn.id this rule sorts by - a reference by id, like DerivedTableFilter, so renaming/reordering columns doesn't lose it. */
  columnId: string;
  /** Sort largest/latest/Z first. Defaults to false (ascending). */
  descending: boolean;
}

export interface DerivedTableRounding {
  enabled: boolean;
  /** Decimal places to round every Number column to. Only meaningful when enabled. */
  places: number | null;
}

export const EMPTY_ROUNDING: DerivedTableRounding = { enabled: false, places: null };

/** Top-level release folder every derived table document is stored under. Hidden from the documents tree. */
export const DERIVED_TABLES_FOLDER = "derivedTables";

/**
 * A Derived table - defines the data transformation from or more child entities into a tabular format
 */
export interface DerivedTable {
  id: string;
  name: string;
  /** The text of the derived table's entity. */
  entity: string | null;
  columns: DerivedTableColumn[];
  tabs: DerivedTableTab[];
  /** Optional for backward compatibility with tables saved before rounding existed - treat a missing value as EMPTY_ROUNDING. */
  rounding?: DerivedTableRounding;
  /** Optional for backward compatibility with tables saved before filters existed - treat a missing value as []. */
  filters?: DerivedTableFilter[];
  /** Optional for backward compatibility with tables saved before ordering existed - treat a missing value as []. */
  ordering?: DerivedTableOrdering[];
}
